/**
 * useScenarioTree — Core data hook replacing FME's state.Input.tree / state.Output.tree.
 * Phase 3B: stores the editor state tree in TanStack Query cache.
 *
 * Query key: ['scenario-tree', scenarioId]
 *
 * The tree data starts empty (TreeInit) and is populated lazily as sections
 * are loaded. Mutations to the tree (field saves, agent patches, WebSocket updates)
 * use queryClient.setQueryData for immutable updates, eliminating the need for
 * the reloadState/mounted toggle pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { TreeInit } from "../../../features/financial-editor/services/dataMappers";

export const SCENARIO_TREE_KEY = "scenario-tree";

export interface EditorState {
  isSync: boolean;
  dashboardComputed: boolean;
  Input: {
    tree: { items: Record<string, any>; rootId: string };
    field_values: Record<string, any>;
  };
  Output: {
    tree: { items: Record<string, any>; rootId: string };
    field_values: Record<string, any>;
  };
}

const createInitialState = (): EditorState => ({
  isSync: false,
  dashboardComputed: false,
  Input: { tree: { ...TreeInit, items: { ...TreeInit.items } }, field_values: {} },
  Output: { tree: { ...TreeInit, items: { ...TreeInit.items } }, field_values: {} },
});

export function useScenarioTree(scenarioId: number) {
  const queryClient = useQueryClient();

  const query = useQuery<EditorState>({
    queryKey: [SCENARIO_TREE_KEY, scenarioId],
    queryFn: () => createInitialState(),
    enabled: scenarioId > 0,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const state = query.data ?? createInitialState();

  const patchTree = useCallback(
    (updater: (prev: EditorState) => EditorState) => {
      queryClient.setQueryData<EditorState>(
        [SCENARIO_TREE_KEY, scenarioId],
        (old) => {
          const prev = old ?? createInitialState();
          return updater(prev);
        }
      );
    },
    [queryClient, scenarioId]
  );

  const setTree = useCallback(
    (newState: EditorState) => {
      queryClient.setQueryData<EditorState>(
        [SCENARIO_TREE_KEY, scenarioId],
        newState
      );
    },
    [queryClient, scenarioId]
  );

  const resetTree = useCallback(() => {
    queryClient.setQueryData<EditorState>(
      [SCENARIO_TREE_KEY, scenarioId],
      createInitialState()
    );
  }, [queryClient, scenarioId]);

  const invalidateTree = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [SCENARIO_TREE_KEY, scenarioId],
    });
  }, [queryClient, scenarioId]);

  return {
    state,
    inputTree: state.Input.tree,
    outputTree: state.Output.tree,
    isLoading: query.isLoading,
    patchTree,
    setTree,
    resetTree,
    invalidateTree,
    queryClient,
  };
}

/** Read tree state directly from cache (non-hook, for use in callbacks). */
export function getTreeFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  scenarioId: number
): EditorState {
  return (
    queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]) ??
    createInitialState()
  );
}

/** Non-hook patchTree — use in effects, callbacks, or wrap in useCallback. */
export function patchTreeCache(
  queryClient: ReturnType<typeof useQueryClient>,
  scenarioId: number,
  updater: (prev: EditorState) => EditorState,
): void {
  queryClient.setQueryData<EditorState>(
    [SCENARIO_TREE_KEY, scenarioId],
    (old) => updater(old ?? createInitialState()),
  );
}

export { createInitialState };
